import { defineStore } from "pinia";
import { SessionItemsApi } from "@simbelapp/order-sdk";
import { useCreateProjectStore } from "../project/create-project.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { ISessionItem } from "~/utils/interfaces/trainings-interface";
import type { ISelectedTraining, IProjectSessionItem } from "~/utils/interfaces/project-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";

export const useSessionItemStore = defineStore("sessionItemStore", {
  getters: {},
  actions: {
    async updateSessionItem(
      training: {
        trainingId: ISelectedTraining | null;
      },
      sessionItem: {
        sessionItemId: IProjectSessionItem | null;
        sessionItemType: string | null;
      },
    ) {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionItemsApi);
        const createProjectStore = useCreateProjectStore();

        const { userId, ...totalBudget } = createProjectStore.getProjectCreateApiPayload?.projectUsers?.length
          ? createProjectStore.getProjectCreateApiPayload?.projectUsers[0]
          : { userId: null };
        const arrayOfBudget = Object.values(totalBudget);
        const initialValue = 0;
        const sumBudget = arrayOfBudget.length
          ? arrayOfBudget.reduce<number>(
              (accumulator, currentValue) => accumulator + (typeof currentValue === "number" ? currentValue : 0),
              initialValue,
            )
          : undefined;
        if (sessionItem.sessionItemId && training.trainingId) {
          await apiInstance.updateSessionItem({
            id: sessionItem.sessionItemId.sessionItemId,
            updateSessionItemDto: {
              trainingId: training.trainingId?.trainingId,
              priceExclTax: sumBudget ?? sessionItem.sessionItemId?.priceExclTax,
              type: sessionItem.sessionItemType ?? undefined,
              modality: sessionItem.sessionItemId?.modality ?? undefined,
            },
          });

          return await this.findAllSessionItems(training.trainingId.trainingId);
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async updateTrainingSessionItem(sessionItemId: string, sessionItems: ISessionItem, trainingId?: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionItemsApi);

        return await apiInstance.updateSessionItem({
          id: sessionItemId,
          updateSessionItemDto: {
            priceExclTax: sessionItems.priceExclTax ?? undefined,
            type: sessionItems.type ?? undefined,
            modality: sessionItems.modality ?? undefined,
            maxNbAttendees: sessionItems.maxNbAttendees ?? undefined,
            minNbAttendees: sessionItems.minNbAttendees ?? undefined,
            overbookingNotAllowed: sessionItems.overbookingNotAllowed,
            ...(trainingId && { trainingId }),
          },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async disableTrainingSessionItem(sessionItemId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionItemsApi);

        await apiInstance.disableSessionItem({
          disableSessionItemDto: { id: sessionItemId },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async createSessionItem(sessionItems: ISessionItem, trainingId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionItemsApi);

        if (!sessionItems.type || !sessionItems.modality) {
          feedback.error(`Une erreur est survenue`, "small");
          return;
        }

        return await apiInstance.createSessionItem({
          createSessionItemDto: {
            trainingId,
            priceExclTax: sessionItems.priceExclTax ?? undefined,
            type: sessionItems.type,
            modality: sessionItems.modality,
            maxNbAttendees: sessionItems.maxNbAttendees ?? undefined,
            minNbAttendees: sessionItems.minNbAttendees ?? undefined,
            overbookingNotAllowed: sessionItems.overbookingNotAllowed,
          },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async findAllSessionItems(trainingId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionItemsApi);
      const sessionItems = await apiInstance.getSessionItemsByTrainingId({ trainingId });
      return sessionItems[0] ?? null;
    },
  },
});
